import type {ISceneManager} from "#template/interfaces/scene-manager"
import {generateImageNodeForInMemoryImageData} from "#template/nodes/image/in-memory-image"
import {transformDecalMask} from "#template/utils/decal-mask"
import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {AnyJsonValue} from "#template/value-types"
import type {ImageNodeGenerator} from "@cm/material-nodes"
import {BsdfPrincipled, Mapping, materialConnection, MaterialGraph, MixShader, OutputMaterial, UvMap} from "@cm/material-nodes"
import {Vector3} from "@cm/math"
import type {TypedImageData} from "@cm/utils"
import {z} from "zod/v4-mini"

const TD = TypeDescriptors

type GetFontMaterialDescriptorType = {
    baseMaterial: InletDescriptor<MaterialGraph | null>
    glyphsImageData: InletDescriptor<TypedImageData>
    glyphsConfiguration: InletDescriptor<AnyJsonValue>
    text: InletDescriptor<string>
    alignment: InletDescriptor<"left" | "center" | "right">
    glyphHeightCm: InletDescriptor<number>
    spacing: InletDescriptor<number | null>
    stamped: InletDescriptor<boolean>
    lightAngle: InletDescriptor<number | null>
    shadowStrength: InletDescriptor<number | null>
    lightStrength: InletDescriptor<number | null>
    embossingSteepness: InletDescriptor<number | null>
    embossingOffset: InletDescriptor<number | null>
    outputMaterial: OutletDescriptor<MaterialGraph | null>
}

const getFontMaterialDescriptor: GetFontMaterialDescriptorType = {
    baseMaterial: TD.inlet(TD.Nullable(TD.MaterialGraph)),
    glyphsImageData: TD.inlet(TD.Identity()),
    glyphsConfiguration: TD.inlet(TD.JSON()),
    text: TD.inlet(TD.String),
    alignment: TD.inlet(TD.Primitive<"left" | "center" | "right">()),
    glyphHeightCm: TD.inlet(TD.Number),
    spacing: TD.inlet(TD.Nullable(TD.Number)),
    stamped: TD.inlet(TD.Boolean),
    lightAngle: TD.inlet(TD.Nullable(TD.Number)),
    shadowStrength: TD.inlet(TD.Nullable(TD.Number)),
    lightStrength: TD.inlet(TD.Nullable(TD.Number)),
    embossingSteepness: TD.inlet(TD.Nullable(TD.Number)),
    embossingOffset: TD.inlet(TD.Nullable(TD.Number)),
    outputMaterial: TD.outlet(TD.Nullable(TD.MaterialGraph)),
}

const configSchema = z.record(z.string(), z.optional(z.object({left: z.number(), top: z.number(), width: z.number(), height: z.number()})))

export class GetFontMaterial implements NodeClassImpl<GetFontMaterialDescriptorType, typeof GetFontMaterial> {
    static descriptor = getFontMaterialDescriptor
    static uniqueName = "GetFontMaterial"
    sceneManager!: Inlet<ISceneManager>
    baseMaterial!: Inlet<MaterialGraph | null>
    glyphsImageData!: Inlet<TypedImageData>
    glyphsConfiguration!: Inlet<AnyJsonValue>
    text!: Inlet<string>
    alignment!: Inlet<"left" | "center" | "right">
    glyphHeightCm!: Inlet<number>
    spacing!: Inlet<number | null>
    stamped!: Inlet<boolean>
    lightAngle!: Inlet<number | null>
    shadowStrength!: Inlet<number | null>
    lightStrength!: Inlet<number | null>
    embossingSteepness!: Inlet<number | null>
    embossingOffset!: Inlet<number | null>
    outputMaterial!: Outlet<MaterialGraph | null>

    run() {
        const baseMaterial = this.baseMaterial
        const glyphsImageData = this.glyphsImageData
        const glyphsConfiguration = this.glyphsConfiguration
        const text = this.text
        const alignment = this.alignment
        const glyphHeightCm = this.glyphHeightCm
        const spacing = this.spacing ?? 0
        const stamped = this.stamped
        const lightAngle = this.lightAngle ?? 0
        const shadowStrength = this.shadowStrength ?? 1
        const lightStrength = this.lightStrength ?? 1
        const embossingSteepness = this.embossingSteepness ?? 1
        const embossingOffset = this.embossingOffset ?? 0
        if (
            baseMaterial === NotReady ||
            glyphsImageData === NotReady ||
            glyphsConfiguration === NotReady ||
            text === NotReady ||
            alignment === NotReady ||
            glyphHeightCm === NotReady ||
            spacing === NotReady ||
            stamped === NotReady ||
            lightAngle === NotReady ||
            shadowStrength === NotReady ||
            lightStrength === NotReady ||
            embossingSteepness === NotReady ||
            embossingOffset === NotReady ||
            typeof glyphsConfiguration !== "object" ||
            glyphsConfiguration === null ||
            Array.isArray(glyphsConfiguration)
        ) {
            this.outputMaterial.emitIfChanged(NotReady)
            return
        }

        const config = configSchema.parse(glyphsConfiguration)

        const characters = [...text].filter((c) => c in config)

        const nPasses = 20
        const borderSize = stamped ? nPasses + 1 : 1

        const width =
            characters.reduce((acc, char, index) => {
                const glyph = config[char]
                if (glyph) {
                    return acc + glyph.width + (index !== characters.length - 1 ? spacing : 0)
                }
                return acc
            }, 0) +
            borderSize * 2
        const glyphHeight = Math.max(...Object.keys(config).map((char) => config[char]?.height ?? 0), 0)

        const height = glyphHeight + borderSize * 2

        const uv = materialConnection(
            new UvMap({
                parameters: {
                    inputs: {},
                    settings: {
                        uvMapIndex: 0,
                    },
                },
            }),
            "uv",
        )

        const scaleY = (height / glyphHeight) * glyphHeightCm
        const scaleX = (width / height) * scaleY

        const shiftX = alignment === "left" ? 0 : alignment === "right" ? -scaleX : -0.5 * scaleX

        const mapping = new Mapping({
            parameters: {
                inputs: {
                    vector: uv,
                },
                settings: {
                    vectorType: "TEXTURE",
                    location: {x: shiftX, y: -0.5 * scaleY, z: 0},
                    rotation: {x: 0, y: 0, z: 0},
                    scale: {x: scaleX, y: scaleY, z: 1},
                },
            },
        })

        const getImageGenerator = (imageData: TypedImageData): ImageNodeGenerator => {
            return {
                generator: ({extension, interpolation, projection, disableProgressiveLoading, forceOriginalResolution}) => {
                    return generateImageNodeForInMemoryImageData(imageData, {
                        uv: materialConnection(mapping, "vector"),
                        extension,
                        interpolation,
                        projection,
                        disableProgressiveLoading,
                        forceOriginalResolution,
                    })
                },
                hash: "",
            }
        }

        const maskCanvas: TypedImageData<Uint8ClampedArray> = {
            data: new Uint8ClampedArray(width * height * 4),
            width,
            height,
            channelLayout: "RGBA",
            dataType: "uint8",
            colorSpace: "linear",
        }

        const buf = maskCanvas.data

        const idx = (x: number, y: number, width: number): number => {
            return y * width + x
        }

        if (
            glyphsImageData.channelLayout !== "RGBA" ||
            glyphsImageData.dataType !== "uint8" ||
            glyphsImageData.colorSpace !== "linear" ||
            !(glyphsImageData.data instanceof Uint8ClampedArray)
        ) {
            throw new Error("Glyphs image data must be in RGBA uint8 linear format")
        }

        let offsetX = borderSize
        for (let i = 0; i < characters.length; i++) {
            const char = characters[i]
            const glyph = config[char]
            if (!glyph) continue

            for (let y = 0; y < glyph.height; y++) {
                for (let x = 0; x < glyph.width; x++) {
                    const srcIndex = idx(glyph.left + x, glyph.top + y, glyphsImageData.width) * 4
                    const dstIndex = idx(offsetX + x, y + borderSize, width) * 4

                    buf[dstIndex] = glyphsImageData.data[srcIndex]
                    buf[dstIndex + 1] = glyphsImageData.data[srcIndex + 1]
                    buf[dstIndex + 2] = glyphsImageData.data[srcIndex + 2]
                    buf[dstIndex + 3] = glyphsImageData.data[srcIndex + 3]
                }
            }

            offsetX += glyph.width + spacing
        }

        if (!stamped) {
            if (!baseMaterial) {
                this.outputMaterial.emitIfChanged(null)
                return
            }

            this.outputMaterial.emitIfChanged(
                transformDecalMask(baseMaterial, {
                    maskImage: getImageGenerator(maskCanvas),
                    widthCm: 1,
                    heightCm: 1,
                    invert: false,
                }),
            )
            return
        }

        let distanceField = new Float32Array(width * height)
        distanceField.fill(Infinity)

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const index = idx(x, y, width)
                const r = buf[4 * index]
                const g = buf[4 * index + 1]
                const b = buf[4 * index + 2]
                if ((r + g + b) / 3 > 128) {
                    distanceField[index] = 0
                }
            }
        }

        const gaussianKernel = [
            [1, 3, 1],
            [3, 9, 3],
            [1, 3, 1],
        ]

        const neighbors = [
            {dx: -1, dy: 0},
            {dx: 1, dy: 0},
            {dx: 0, dy: -1},
            {dx: 0, dy: 1},
        ]

        for (let pass = 0; pass < nPasses; pass++) {
            for (let y = 0; y < height; y++) {
                for (let x = 0; x < width; x++) {
                    const index = idx(x, y, width)
                    if (distanceField[index] !== pass) continue

                    for (const n of neighbors) {
                        const nx = x + n.dx
                        const ny = y + n.dy
                        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue
                        const nIndex = idx(nx, ny, width)
                        const nDist = distanceField[nIndex]
                        if (nDist === Infinity) distanceField[nIndex] = pass + 1
                    }
                }
            }
        }

        const smoothStep = (x: number, n: number): number => {
            return Math.pow(x, n) / (Math.pow(x, n) + Math.pow(1 - x, n))
        }

        const exp = Math.abs(embossingOffset) > 1 ? (embossingOffset < 0 ? 1 / -embossingOffset : embossingOffset) : 1

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const index = idx(x, y, width)
                const dist = distanceField[index]
                if (dist === Infinity) distanceField[index] = 0.0
                else distanceField[index] = 1 - smoothStep(Math.pow(dist / nPasses, exp), embossingSteepness)
            }
        }

        //apply some gaussian filtering
        for (let i = 0; i < 10; i++) {
            const newDistanceField = new Float32Array(width * height)
            for (let y = 0; y < height; y++) {
                for (let x = 0; x < width; x++) {
                    let sum = 0
                    let weightSum = 0
                    for (let ky = -1; ky <= 1; ky++) {
                        for (let kx = -1; kx <= 1; kx++) {
                            const nx = x + kx
                            const ny = y + ky
                            if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue
                            const nIndex = idx(nx, ny, width)
                            const weight = gaussianKernel[ky + 1][kx + 1]
                            sum += distanceField[nIndex] * weight
                            weightSum += weight
                        }
                    }
                    newDistanceField[idx(x, y, width)] = sum / weightSum
                }
            }
            distanceField = newDistanceField
        }

        const lightAngleRad = (lightAngle * Math.PI) / 180

        const lightVector = new Vector3(Math.cos(lightAngleRad), Math.sin(lightAngleRad), 0).normalized()

        const normalStrength = (scaleX / width) * 100
        const shadowCanvas: TypedImageData<Uint8ClampedArray> = {
            data: new Uint8ClampedArray(width * height * 4),
            width,
            height,
            channelLayout: "RGBA",
            dataType: "uint8",
            colorSpace: "linear",
        }
        const sbuf = shadowCanvas.data
        const shadowAlphaCanvas: TypedImageData<Uint8ClampedArray> = {
            data: new Uint8ClampedArray(width * height * 4),
            width,
            height,
            channelLayout: "RGBA",
            dataType: "uint8",
            colorSpace: "linear",
        }
        const sabuf = shadowAlphaCanvas.data

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const index = idx(x, y, width)
                if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
                    sbuf[index * 4] = 255
                    sbuf[index * 4 + 1] = 255
                    sbuf[index * 4 + 2] = 255
                    sbuf[index * 4 + 3] = 255

                    sabuf[index * 4] = 0
                    sabuf[index * 4 + 1] = 0
                    sabuf[index * 4 + 2] = 0
                    sabuf[index * 4 + 3] = 255
                    continue
                }

                const dx = distanceField[idx(x + 1, y, width)] - distanceField[idx(x - 1, y, width)]
                const dy = distanceField[idx(x, y + 1, width)] - distanceField[idx(x, y - 1, width)]

                const normalLight = new Vector3(-dx * normalStrength, -dy * normalStrength, 1.0).normalized()

                const nDotL = normalLight.dot(lightVector)

                if (nDotL < 0) {
                    sbuf[index * 4] = 0
                    sbuf[index * 4 + 1] = 0
                    sbuf[index * 4 + 2] = 0

                    const alpha = Math.abs(nDotL) * 255 * shadowStrength
                    sabuf[index * 4] = alpha
                    sabuf[index * 4 + 1] = alpha
                    sabuf[index * 4 + 2] = alpha
                } else {
                    sbuf[index * 4] = 255
                    sbuf[index * 4 + 1] = 255
                    sbuf[index * 4 + 2] = 255

                    const alpha = Math.abs(nDotL) * 255 * lightStrength
                    sabuf[index * 4] = alpha
                    sabuf[index * 4 + 1] = alpha
                    sabuf[index * 4 + 2] = alpha
                }

                sbuf[index * 4 + 3] = 255
                sabuf[index * 4 + 3] = 255
            }
        }

        const shader = new BsdfPrincipled({
            parameters: {
                inputs: {
                    baseColor: getImageGenerator(shadowCanvas).generator({
                        uv,
                        extension: "EXTEND",
                        interpolation: "Linear",
                        projection: "FLAT",
                    }).color,
                    alpha: getImageGenerator(shadowAlphaCanvas).generator({
                        uv,
                        extension: "EXTEND",
                        interpolation: "Linear",
                        projection: "FLAT",
                    }).color,
                },
                settings: {},
            },
        })

        const getSurface = () => {
            if (!baseMaterial) return materialConnection(shader, "bsdf")

            const mixShader = new MixShader({
                parameters: {
                    inputs: {
                        fac: getImageGenerator(maskCanvas).generator({
                            uv,
                            extension: "EXTEND",
                            interpolation: "Linear",
                            projection: "FLAT",
                        }).color,
                        shader: materialConnection(shader, "bsdf"),
                        shader_001: baseMaterial.rootNode!.parameters.inputs.surface,
                    },
                    settings: {},
                },
            })
            return materialConnection(mixShader, "shader")
        }

        const output = new OutputMaterial({
            parameters: {
                inputs: {
                    surface: getSurface(),
                },
                settings: {},
            },
        })

        const materialGraph = MaterialGraph.fromOutputMaterial(output, `FontMaterial-${text}`)
        this.outputMaterial.emitIfChanged(materialGraph)
    }
}
